(function () {
  "use strict";

  var VERSION = "p11dz686-push-foreground-delivery-20261015-r6";
  var API_URL = "/apps/dsd-firebase-custom-token/supabase/notification-push";
  var WORKER_URL = "/apps/dsd-firebase-custom-token/push/service-worker.js";
  var WORKER_SCOPE = "/apps/dsd-firebase-custom-token/push/";
  var ACTOR_KEY = "dsd.nativePush.actor.v1";
  var state = { status: null, pending: false, isolation: Promise.resolve(false) };

  function customer() {
    return window.DSDShopifyCustomer && window.DSDShopifyCustomer.authenticated
      ? window.DSDShopifyCustomer
      : null;
  }

  function actorKey() {
    var value = customer() && (customer().profileKey || customer().handle) || "";
    return String(value).trim().toLowerCase();
  }

  function supported() {
    return !!(
      window.isSecureContext &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  }

  function permission() {
    return "Notification" in window ? Notification.permission : "unsupported";
  }

  function api(action, payload, method) {
    var options = {
      method: method || "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" }
    };
    if (options.method !== "GET") options.body = JSON.stringify(Object.assign({ action: action }, payload || {}));
    return fetch(API_URL, options).then(function (response) {
      return response.text().then(function (text) {
        var body = {};
        try { body = text ? JSON.parse(text) : {}; } catch (_error) { body = {}; }
        if (!response.ok || body.ok === false) {
          var error = new Error(body.error || "push_request_failed");
          error.status = response.status;
          throw error;
        }
        return body;
      });
    });
  }

  function applicationServerKey(value) {
    var padding = "=".repeat((4 - String(value).length % 4) % 4);
    var base64 = (String(value) + padding).replace(/-/g, "+").replace(/_/g, "/");
    var raw = window.atob(base64);
    return Uint8Array.from(Array.prototype.map.call(raw, function (char) { return char.charCodeAt(0); }));
  }

  function activeRegistration(worker) {
    if (worker.active) return Promise.resolve(worker);
    var candidate = worker.installing || worker.waiting;
    if (!candidate) return Promise.reject(new Error("push_worker_not_active"));
    return new Promise(function (resolve, reject) {
      var timeout = window.setTimeout(function () {
        candidate.removeEventListener("statechange", onState);
        reject(new Error("push_worker_activation_timeout"));
      }, 10000);
      function onState() {
        if (candidate.state === "activated") {
          window.clearTimeout(timeout);
          candidate.removeEventListener("statechange", onState);
          resolve(worker);
        } else if (candidate.state === "redundant") {
          window.clearTimeout(timeout);
          candidate.removeEventListener("statechange", onState);
          reject(new Error("push_worker_install_failed"));
        }
      }
      candidate.addEventListener("statechange", onState);
      onState();
    });
  }

  function registration() {
    if (!supported()) return Promise.reject(new Error("push_unsupported"));
    return navigator.serviceWorker.register(WORKER_URL, { scope: WORKER_SCOPE }).then(activeRegistration);
  }

  function exactRegistration() {
    if (!supported()) return Promise.resolve(null);
    var expectedScope = new URL(WORKER_SCOPE, window.location.origin).href;
    return navigator.serviceWorker.getRegistrations().then(function (workers) {
      var exact = Array.prototype.find.call(workers || [], function (worker) {
        return worker && worker.scope === expectedScope;
      });
      return exact || navigator.serviceWorker.getRegistration(WORKER_SCOPE);
    });
  }

  function currentSubscription() {
    if (!supported()) return Promise.resolve(null);
    return exactRegistration().then(function (worker) {
      return worker ? worker.pushManager.getSubscription() : null;
    });
  }

  function refreshExistingWorker() {
    if (!supported()) return Promise.resolve(null);
    return exactRegistration().then(function (worker) {
      return worker && typeof worker.update === "function" ? worker.update() : null;
    }).catch(function () { return null; });
  }

  function actorMarker() {
    try { return localStorage.getItem(ACTOR_KEY) || ""; } catch (_error) { return ""; }
  }

  function setActorMarker(value) {
    try {
      if (value) localStorage.setItem(ACTOR_KEY, value);
      else localStorage.removeItem(ACTOR_KEY);
    } catch (_error) {}
  }

  function emit() {
    window.dispatchEvent(new CustomEvent("dsd:native-push:state", { detail: snapshot() }));
  }

  function safeForegroundNotification(value) {
    value = value || {};
    if (value.type !== "dsd:native-push:notification") return null;
    var href = String(value.href || "");
    try {
      var target = new URL(href, window.location.origin);
      if (target.origin !== window.location.origin || !/^\/pages\//.test(target.pathname)) return null;
      return {
        href: target.pathname + target.search + target.hash,
        notificationId: String(value.notificationId || ""),
        notificationKind: value.notificationKind === "call" ? "call" : "notification"
      };
    } catch (_error) {
      return null;
    }
  }

  function onServiceWorkerMessage(event) {
    var detail = safeForegroundNotification(event && event.data);
    if (!detail) return;
    window.dispatchEvent(new CustomEvent("dsd:native-push:notification", { detail: detail }));
  }

  function snapshot() {
    return {
      version: VERSION,
      supported: supported(),
      permission: permission(),
      pending: state.pending,
      status: state.status
    };
  }

  function loadStatus() {
    if (!customer()) return Promise.resolve(null);
    state.pending = true;
    emit();
    return Promise.resolve(state.isolation).then(function () {
      return api("status", null, "GET");
    }).then(function (result) {
      state.status = result;
      return currentSubscription().then(function (subscription) {
        state.status.currentDeviceSubscribed = false;
        state.status.reconciliationReason = subscription ? "checking" : "browser_subscription_missing";
        state.status.rebindRequired = false;
        if (!subscription) {
          if (actorMarker() === actorKey()) setActorMarker("");
          return state.status;
        }
        return api("reconcile", { subscription: subscription.toJSON() }).then(function (reconciled) {
          state.status.currentDeviceSubscribed = reconciled.associated === true;
          state.status.currentDeviceEndpointId = reconciled.endpointId || "";
          state.status.reconciliationReason = reconciled.reason || "associated";
          state.status.rebindRequired = reconciled.rebindRequired === true;
          if (state.status.currentDeviceSubscribed) setActorMarker(actorKey());
          return state.status;
        }).catch(function (error) {
          if (error && error.status === 409) {
            state.status.currentDeviceSubscribed = false;
            state.status.reconciliationReason = error.message || "rebind_required";
            state.status.rebindRequired = true;
            return state.status;
          }
          throw error;
        });
      });
    }).finally(function () {
      state.pending = false;
      emit();
    });
  }

  function savePreferences(preferences) {
    if (!customer()) return Promise.reject(new Error("logged_in_customer_required"));
    state.pending = true;
    emit();
    return api("save_preferences", { preferences: preferences || {} }).then(function (result) {
      state.status = Object.assign({}, state.status || {}, result);
      return result;
    }).finally(function () {
      state.pending = false;
      emit();
    });
  }

  function enable() {
    if (!customer()) return Promise.reject(new Error("logged_in_customer_required"));
    if (!supported()) return Promise.reject(new Error("push_unsupported"));
    state.pending = true;
    emit();
    return loadStatus().then(function (status) {
      if (!status || !status.configured || !status.publicKey) throw new Error("push_delivery_not_configured");
      return registration().then(function (worker) {
        var permissionPromise = permission() === "granted"
          ? Promise.resolve("granted")
          : permission() === "denied"
            ? Promise.reject(new Error("push_permission_denied"))
            : Notification.requestPermission();
        return permissionPromise.then(function (result) {
          if (result !== "granted") throw new Error(result === "denied" ? "push_permission_denied" : "push_permission_not_granted");
          return worker.pushManager.getSubscription().then(function (existing) {
            var replace = !!(existing && status && status.rebindRequired);
            return (replace ? existing.unsubscribe().then(function () { return null; }) : Promise.resolve(existing)).then(function (usable) {
              return usable || worker.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationServerKey(status.publicKey) });
            });
          });
        });
      }).then(function (subscription) {
        return api("subscribe", { subscription: subscription.toJSON() }).then(function () {
          setActorMarker(actorKey());
          return savePreferences(Object.assign({}, state.status && state.status.preferences || {}, { pushEnabled: true }));
        });
      }).then(loadStatus).finally(function () {
        state.pending = false;
        emit();
      });
    });
  }

  function disable() {
    state.pending = true;
    emit();
    return currentSubscription().then(function (subscription) {
      var revoke = subscription && customer()
        ? api("unsubscribe", { endpoint: subscription.endpoint }).catch(function () { return null; })
        : Promise.resolve(null);
      return revoke.then(function () {
        return subscription ? subscription.unsubscribe() : false;
      });
    }).then(function () {
      setActorMarker("");
      return customer()
        ? savePreferences(Object.assign({}, state.status && state.status.preferences || {}, { pushEnabled: false }))
        : null;
    }).then(loadStatus).finally(function () {
      state.pending = false;
      emit();
    });
  }

  function isolateAccount() {
    if (!supported()) return Promise.resolve(false);
    var prior = actorMarker();
    var signedCustomer = customer();
    var current = actorKey();
    if (!prior) return Promise.resolve(false);
    if (signedCustomer && !current) return Promise.resolve(false);
    if (current && prior === current) return Promise.resolve(false);
    return currentSubscription().then(function (subscription) {
      return subscription ? subscription.unsubscribe() : false;
    }).finally(function () {
      setActorMarker("");
      state.status = null;
      emit();
    });
  }

  state.isolation = isolateAccount();
  if (supported()) navigator.serviceWorker.addEventListener("message", onServiceWorkerMessage);
  refreshExistingWorker();
  window.addEventListener("dsd:launch-social:ready", function () {
    state.isolation = Promise.resolve(state.isolation).then(isolateAccount);
  }, { once: true });

  window.DSDNativePush = {
    version: VERSION,
    supported: supported,
    snapshot: snapshot,
    loadStatus: loadStatus,
    savePreferences: savePreferences,
    enable: enable,
    disable: disable
  };
})();
